import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Drawer } from '@arco-design/web-react'
import { IconArrowRight, IconBook, IconDashboard, IconHome } from '@arco-design/web-react/icon'
import { Menu as MenuIcon } from 'lucide-react'
import LanguageSwitcher from '@/components/Common/LanguageSwitcher'
import { getUserDisplayName, getUserInitial } from '@/lib/userDisplayName'
import { useAuthStore } from '@/stores/authStore'
import type { AuthPanelView } from '@/components/Auth/AuthPanel'
import '@/styles/public-page.css'

export type PublicContentWidth = 'docs' | 'about' | 'legal' | 'legal-wide' | 'full'

export interface PublicPageShellProps {
  children: ReactNode
  contentWidth?: PublicContentWidth
  className?: string
  mainClassName?: string
}

const widthClassMap: Record<PublicContentWidth, string> = {
  docs: 'public-main--docs',
  about: 'public-main--about',
  legal: 'public-main--legal',
  'legal-wide': 'public-main--legal-wide',
  full: 'public-main--full',
}

export default function PublicPageShell({
  children,
  contentWidth = 'about',
  className = '',
  mainClassName = '',
}: PublicPageShellProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const accountLabel = getUserDisplayName(user, t('common.console'))
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const goHome = () => navigate('/')
  const goDocs = () => navigate('/docs')
  const goConsole = () => navigate('/console')

  const openAuth = (authView: AuthPanelView = 'register') => {
    navigate('/', { state: { openAuth: true, authView } })
  }

  const desktopAuthActions = isAuthenticated ? (
    <button
      type="button"
      className="public-nav-account-chip hidden sm:inline-flex"
      onClick={goConsole}
      aria-label={t('common.enterConsole')}
    >
      <span className="public-nav-account-avatar" aria-hidden>
        {getUserInitial(user)}
      </span>
      <span className="public-nav-account-name">{accountLabel}</span>
      <IconArrowRight className="public-nav-account-arrow" />
    </button>
  ) : (
    <>
      <button type="button" className="public-btn-ghost hidden sm:inline-flex" onClick={() => openAuth('login')}>
        {t('auth.login')}
      </button>
      <button type="button" className="public-btn-primary hidden sm:inline-flex" onClick={() => openAuth('register')}>
        {t('landing.getStarted')}
      </button>
    </>
  )

  return (
    <div className={`public-page ${className}`.trim()}>
      <div className="public-page-bg" aria-hidden>
        <div className="public-page-bg-mesh public-page-bg-mesh-indigo" />
        <div className="public-page-bg-mesh public-page-bg-mesh-cyan" />
      </div>

      <div className="public-page-inner">
        <header className="public-topbar">
          <div className="public-topbar-inner">
            <button
              type="button"
              className="flex items-center gap-3 border-0 bg-transparent p-0 cursor-pointer min-w-0"
              onClick={goHome}
              aria-label={t('common.backHome')}
            >
              <img src="/logo.svg" alt="TokenTCP" className="w-8 h-8 shrink-0" />
              <span className="text-[18px] font-extrabold tracking-[-0.02em] text-[#0F172A] truncate">
                <span>Token</span>
                <span className="public-logo-accent">TCP</span>
              </span>
            </button>

            <div className="public-topbar-actions">
              <button
                type="button"
                aria-label={t('common.openNavMenu')}
                className="public-mobile-menu-btn sm:hidden"
                onClick={() => setMobileNavOpen(true)}
              >
                <MenuIcon size={18} />
              </button>
              <button
                type="button"
                className="public-btn-ghost hidden sm:inline-flex"
                onClick={goHome}
              >
                <IconHome />
                {t('common.backHome')}
              </button>
              <button
                type="button"
                className="public-nav-icon-btn hidden sm:inline-flex"
                onClick={goDocs}
                aria-label={t('nav.docs')}
              >
                <IconBook />
              </button>
              <LanguageSwitcher variant="landing" />
              <div className="hidden sm:flex items-center gap-2">{desktopAuthActions}</div>
            </div>
          </div>
        </header>

        <Drawer
          visible={mobileNavOpen}
          placement="right"
          width={280}
          title={t('common.navigation')}
          footer={null}
          onCancel={() => setMobileNavOpen(false)}
          className="public-mobile-nav-drawer sm:hidden"
        >
          <nav className="public-drawer-nav">
            <button
              type="button"
              className="public-drawer-link"
              onClick={() => {
                setMobileNavOpen(false)
                goHome()
              }}
            >
              {t('common.backHome')}
            </button>
            <button
              type="button"
              className="public-drawer-link"
              onClick={() => {
                setMobileNavOpen(false)
                goDocs()
              }}
            >
              {t('landing.viewDocs')}
            </button>
            {isAuthenticated ? (
              <>
                <div className="public-mobile-account px-4 py-3 rounded-lg bg-[#EEF2FF]/60 border border-[#C7D2FE]/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="public-nav-account-avatar">{getUserInitial(user)}</span>
                    <span className="public-nav-account-name text-[15px]">{accountLabel}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="public-btn-primary w-full justify-center"
                  onClick={() => {
                    setMobileNavOpen(false)
                    goConsole()
                  }}
                >
                  <IconDashboard />
                  {t('common.enterConsole')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="public-btn-secondary w-full justify-center"
                  onClick={() => {
                    setMobileNavOpen(false)
                    openAuth('login')
                  }}
                >
                  {t('auth.login')}
                </button>
                <button
                  type="button"
                  className="public-btn-primary w-full justify-center"
                  onClick={() => {
                    setMobileNavOpen(false)
                    openAuth('register')
                  }}
                >
                  {t('landing.getStarted')}
                </button>
              </>
            )}
          </nav>
        </Drawer>

        <main className={`public-main ${widthClassMap[contentWidth]} ${mainClassName}`.trim()}>
          {children}
        </main>

        <footer className="public-footer">
          <div className="public-footer-inner">
            <span className="public-footer-copy">© 2026 TokenTCP. All rights reserved.</span>
            <nav className="public-footer-nav" aria-label={t('common.navigation')}>
              <button type="button" className="public-footer-link" onClick={goDocs}>
                {t('landing.footer.docs')}
              </button>
              <button type="button" className="public-footer-link" onClick={() => navigate('/about')}>
                {t('landing.footer.about')}
              </button>
              <button type="button" className="public-footer-link" onClick={() => navigate('/agreement')}>
                {t('landing.footer.agreement')}
              </button>
              <button type="button" className="public-footer-link" onClick={() => navigate('/policy')}>
                {t('landing.footer.policy')}
              </button>
            </nav>
          </div>
        </footer>
      </div>
    </div>
  )
}
